import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "onchainreps-theme";

function readStoredMode(): ThemeMode {
  if (typeof localStorage === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);
  const [resolved, setResolved] = useState<"light" | "dark">(() =>
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  );

  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    const apply = () => {
      const next: "light" | "dark" = mode === "system" ? (media.matches ? "dark" : "light") : mode;
      root.dataset.theme = next;
      root.style.colorScheme = next;
      setResolved(next);
    };

    apply();
    localStorage.setItem(STORAGE_KEY, mode);
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((current) => {
      if (current === "system") return resolved === "dark" ? "light" : "dark";
      return current === "dark" ? "light" : "dark";
    });
  }, [resolved]);

  return { mode, resolved, setMode, toggle };
}
